import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Search, Upload, Link2, Unlink, Trash2, Image as ImageIcon, Check } from "lucide-react";
import { toast } from "sonner";
import api, { salesClientsApi } from "../lib/api";
import { cn } from "../lib/utils";
import ClientLogo from "./ClientLogo";

/**
 * The logo library.
 *
 * It exists because the studio's logo pack is wider than the client database:
 * marks for one-off tours, for sub-brands, and for companies that never became
 * a client record. Those have nowhere to live on /clients, so they live here,
 * and linking one to a client is a two-click job from this grid.
 */

// Which client a mark belongs to — a filtered list beats a 250-row <Select>.
function ClientPicker({ clients, onPick, children }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");

  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const pool = needle
      ? clients.filter((c) => (c.name || "").toLowerCase().includes(needle))
      : clients;
    return pool.slice(0, 60);
  }, [clients, q]);

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-0 bg-zinc-900 border-zinc-700" align="start">
        <div className="p-2 border-b border-zinc-800">
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find client..."
            className="h-8 text-sm bg-zinc-800 border-zinc-700 text-zinc-50"
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {matches.length === 0 ? (
            <p className="px-3 py-4 text-xs text-zinc-500 text-center">No client matches</p>
          ) : (
            matches.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { onPick(c); setOpen(false); setQ(""); }}
                className="w-full text-left px-3 py-1.5 text-sm text-zinc-200 hover:bg-zinc-800 flex items-center justify-between gap-2"
              >
                <span className="truncate">{c.name}</span>
                {c.country && <span className="text-[10px] text-zinc-500 shrink-0">{c.country}</span>}
              </button>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function ClientLogoLibrary({ open, onClose, clients = [], canEdit, onChanged }) {
  const [logos, setLogos] = useState([]);
  // The library loads its OWN client list rather than reusing the page's.
  // /clients passes whatever its type filter is showing, so with the filter on
  // "Sales" a legacy client like Vertical Development was simply absent from
  // the picker — you searched for it and got "nothing matches", with no hint
  // that the page filter was the reason.
  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | linked | unlinked
  const [variant, setVariant] = useState("branded"); // which file to preview
  const [busyId, setBusyId] = useState(null);
  const uploadRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/client-logos");
      setLogos(res.data?.logos || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load logos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    load();
    salesClientsApi
      .getAll("any")
      .then((res) => setAllClients(res.data || []))
      .catch(() => setAllClients([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fall back to the page's list only until our own arrives.
  const pickerClients = allClients.length ? allClients : clients;

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return logos.filter((l) => {
      const linkedCount = l.client_ids?.length ?? (l.client_id ? 1 : 0);
      if (filter === "linked" && !linkedCount) return false;
      if (filter === "unlinked" && linkedCount) return false;
      if (!needle) return true;
      return (
        (l.name || "").toLowerCase().includes(needle) ||
        (l.client_names || [l.client_name]).some((n) => (n || "").toLowerCase().includes(needle)) ||
        (l.country || "").toLowerCase().includes(needle)
      );
    });
  }, [logos, q, filter]);

  const counts = useMemo(() => ({
    all: logos.length,
    linked: logos.filter((l) => (l.client_ids?.length ?? (l.client_id ? 1 : 0))).length,
    unlinked: logos.filter((l) => !(l.client_ids?.length ?? (l.client_id ? 1 : 0))).length,
  }), [logos]);

  const patch = async (logo, body, okMsg) => {
    setBusyId(logo.id);
    try {
      const res = await api.put(`/client-logos/${logo.id}`, body);
      setLogos((prev) => prev.map((l) => (l.id === logo.id ? res.data.logo : l)));
      toast.success(okMsg);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update logo");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (logo) => {
    if (!window.confirm(`Delete the logo "${logo.name}"? The image files go too.`)) return;
    setBusyId(logo.id);
    try {
      await api.delete(`/client-logos/${logo.id}`);
      setLogos((prev) => prev.filter((l) => l.id !== logo.id));
      toast.success("Logo deleted");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete logo");
    } finally {
      setBusyId(null);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    // Filename is the best name we have; it is editable afterwards.
    form.append("name", file.name.replace(/\.[^.]+$/, ""));
    form.append("variant", variant);
    try {
      await api.post("/client-logos", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo uploaded");
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-amber-400" />
            Client logos
            <span className="text-xs font-normal text-zinc-500">
              {counts.all} marks · {counts.linked} on a client · {counts.unlinked} unlinked
            </span>
          </DialogTitle>
        </DialogHeader>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 p-0.5 text-xs shrink-0">
            {[
              { val: "all", label: "All" },
              { val: "linked", label: "Linked" },
              { val: "unlinked", label: "Unlinked" },
            ].map((opt) => (
              <button
                key={opt.val}
                onClick={() => setFilter(opt.val)}
                className={cn(
                  "px-3 py-1 rounded transition-colors font-medium flex items-center gap-1.5",
                  filter === opt.val ? "bg-orange-500 text-white" : "text-zinc-400 hover:text-zinc-200"
                )}
              >
                {opt.label}
                <span className={cn("text-[10px] tabular-nums", filter === opt.val ? "text-orange-100" : "text-zinc-500")}>
                  {counts[opt.val]}
                </span>
              </button>
            ))}
          </div>

          {/* Branded is what the portal draws; plain is the original the client sent. */}
          <div className="inline-flex items-center rounded-md border border-zinc-700 bg-zinc-900/50 p-0.5 text-xs shrink-0">
            {[
              { val: "branded", label: "Branded" },
              { val: "plain", label: "Original" },
            ].map((opt) => (
              <button
                key={opt.val}
                onClick={() => setVariant(opt.val)}
                className={cn(
                  "px-3 py-1 rounded transition-colors font-medium",
                  variant === opt.val ? "bg-zinc-700 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by logo, client or country..."
              className="pl-10 h-9 bg-zinc-900 border-zinc-800 text-zinc-50"
            />
          </div>

          {canEdit && (
            <>
              <input
                ref={uploadRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                className="hidden"
                onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }}
              />
              <Button
                size="sm"
                variant="outline"
                className="border-zinc-700 h-9"
                onClick={() => uploadRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />Add logo
              </Button>
            </>
          )}
        </div>

        {/* Grid */}
        <div className="overflow-y-auto -mx-1 px-1 mt-1">
          {loading ? (
            <p className="text-zinc-500 py-10 text-center text-sm">Loading...</p>
          ) : shown.length === 0 ? (
            <p className="text-zinc-500 py-10 text-center text-sm">No logos match</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
              {shown.map((logo) => {
                const src = variant === "plain" ? (logo.plain_url || logo.branded_url) : (logo.branded_url || logo.plain_url);
                const busy = busyId === logo.id;
                const ids = logo.client_ids?.length ? logo.client_ids : (logo.client_id ? [logo.client_id] : []);
                const names = logo.client_names?.length ? logo.client_names : (logo.client_name ? [logo.client_name] : []);
                const owners = ids.map((id, i) => ({ id, name: names[i] || id }));
                return (
                  <div
                    key={logo.id}
                    className={cn(
                      "rounded-lg border bg-zinc-900/60 p-2 flex flex-col gap-2 transition-opacity",
                      ids.length ? "border-zinc-800" : "border-amber-500/30",
                      busy && "opacity-50"
                    )}
                  >
                    <ClientLogo
                      src={src}
                      name={logo.name}
                      variant={src === logo.branded_url ? "branded" : "plain"}
                      className="w-full h-20"
                      iconClassName="h-6 w-6"
                    />
                    <div className="min-w-0">
                      <p className="text-sm text-zinc-100 truncate" title={logo.name}>{logo.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        {logo.country && (
                          <span className="text-[10px] text-zinc-500 truncate">{logo.country}</span>
                        )}
                        {!logo.branded_url && <Badge className="text-[9px] bg-zinc-700 text-zinc-300">original only</Badge>}
                        {!logo.plain_url && <Badge className="text-[9px] bg-zinc-700 text-zinc-300">branded only</Badge>}
                      </div>
                    </div>

                    {/* One mark can belong to several records — BI Group is five
                        rows in the client list and ships one file. */}
                    <div className="space-y-1">
                      {owners.map((o) => (
                        <div key={o.id} className="flex items-center gap-1">
                          <span className="flex-1 min-w-0 text-xs text-emerald-300 flex items-center gap-1" title={o.name}>
                            <Check className="h-3 w-3 shrink-0" />
                            <span className="truncate">{o.name}</span>
                          </span>
                          {canEdit && (
                            <Button
                              size="sm" variant="ghost" disabled={busy}
                              className="h-6 w-6 p-0 text-zinc-500 hover:text-amber-300"
                              title={`Unlink from ${o.name}`}
                              onClick={() => patch(logo, { remove_client_id: o.id }, `Unlinked from ${o.name}`)}
                            >
                              <Unlink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center gap-1">
                        {canEdit ? (
                          <ClientPicker
                            clients={pickerClients}
                            onPick={(c) => patch(logo, { client_id: c.id }, `Linked to ${c.name}`)}
                          >
                            <Button
                              size="sm" variant="outline" disabled={busy}
                              className={cn(
                                "h-6 flex-1 text-[11px]",
                                owners.length
                                  ? "border-zinc-700 text-zinc-400 hover:text-zinc-200"
                                  : "border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                              )}
                            >
                              <Link2 className="h-3 w-3 mr-1" />
                              {owners.length ? "Link another" : "Link to client"}
                            </Button>
                          </ClientPicker>
                        ) : owners.length === 0 ? (
                          <span className="flex-1 text-[11px] text-zinc-600">Not linked</span>
                        ) : null}
                        {canEdit && (
                          <Button
                            size="sm" variant="ghost" disabled={busy}
                            className="h-6 w-6 p-0 text-zinc-600 hover:text-red-400"
                            title="Delete logo"
                            onClick={() => remove(logo)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
